import { Marked } from "marked";
// Relative import (not the @ alias) so the node test runner can resolve it too
import { createSafeMarkdownRenderer } from "../aiQuery/aiQueryContentSecurity.js";

// Callout blockquotes (GitHub/Obsidian-style): a blockquote whose first line
// is `[!type]` renders as a styled callout box. Types map to the app palette
// in resources/sass/parts/_kbArticle.scss.
const CALLOUTS = {
  note: { icon: "fas fa-info-circle", label: "Note" },
  tip: { icon: "fas fa-lightbulb", label: "Tip" },
  important: { icon: "fas fa-exclamation-circle", label: "Important" },
  warning: { icon: "fas fa-exclamation-triangle", label: "Warning" },
  caution: { icon: "fas fa-times-circle", label: "Caution" },
};

const CALLOUT_MARKER = /^\[!(\w+)\]\s*/;

// Image size presets: `![caption|small](url)` — widths live in _kbArticle.scss
const IMAGE_SIZE = /^(.*?)\|(small|medium|large)$/;

const safeRenderer = createSafeMarkdownRenderer({ allowImages: true });

const articleRenderer = {
  ...safeRenderer,

  image(token) {
    const match = IMAGE_SIZE.exec(token.text ?? "");
    const html = safeRenderer.image(match ? { ...token, text: match[1] } : token);
    return match && html.startsWith("<img ") ? html.replace("<img ", `<img class="kb-img-${match[2]}" `) : html;
  },

  blockquote({ tokens }) {
    const firstInline = tokens[0]?.type === "paragraph" ? tokens[0].tokens?.[0] : null;
    const match = firstInline?.type === "text" ? CALLOUT_MARKER.exec(firstInline.text) : null;
    const callout = match ? CALLOUTS[match[1].toLowerCase()] : null;
    if (!callout) return false; // plain blockquote — default rendering

    // Strip the marker so it doesn't appear in the rendered body
    firstInline.text = firstInline.text.slice(match[0].length);
    if (firstInline.raw.startsWith(match[0])) firstInline.raw = firstInline.raw.slice(match[0].length);

    const body = this.parser.parse(tokens);
    const type = match[1].toLowerCase();

    return `<div class="kb-callout kb-callout-${type}"><p class="kb-callout-title"><i class="${callout.icon}"></i> ${callout.label}</p>${body}</div>`;
  },
};

// Single renderer for article markdown everywhere it appears: the customer
// article view, the help drawer, and the kiosk editor preview. Raw HTML is
// escaped and link/image URLs are protocol-restricted; images are allowed
// because article content is author-controlled.
const articleMarked = new Marked({ renderer: articleRenderer });

export function renderArticleMarkdown(markdown) {
  return articleMarked.parse(markdown ?? "");
}
